import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import EmailOtpVerify from "../components/EmailOtpVerify";
import GoogleButton from "../components/GoogleButton";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(location.state?.from?.pathname || "/app");
    } catch (err) {
      // Backend returns 403 with { code: 'email_not_verified', email } when the
      // account exists but the email has not yet been verified.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setPendingEmail(detail.email || email);
        toast.info("Verify your email — we sent you a fresh code");
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Login failed");
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const error = new URLSearchParams(location.search).get("error");

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand */}
      <div className="hidden lg:flex flex-col justify-between bg-zinc-900 text-white p-12 grid-bg">
        <Link to="/" className="font-display text-2xl font-bold">EXAM<span className="text-blue-400">/</span>OS</Link>
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-4 font-semibold">Trusted by institutions</div>
          <div className="font-display text-4xl font-bold tracking-tight leading-tight">
            The infrastructure that powers honest assessment.
          </div>
          <div className="mt-6 text-sm text-zinc-400 max-w-md">
            Built for universities, training programs, and certification bodies that take exam integrity seriously.
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">SOC 2 Ready · WCAG 2.1 AA</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        {pendingEmail ? (
          <EmailOtpVerify
            email={pendingEmail}
            onVerified={() => navigate("/app")}
            onChangeEmail={() => setPendingEmail("")}
          />
        ) : (
        <div className="w-full max-w-sm">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Sign in</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Welcome back</h1>
          <p className="text-sm text-zinc-500 mb-8">Sign in with your registered email or Google account.</p>

          {error && (
            <div className="mb-4 border border-red-200 bg-red-50 text-red-700 text-xs p-3" data-testid="login-error">
              Google sign-in failed. Try again or use email.
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</label>
                <Link to="/forgot-password" className="text-[11px] text-blue-600 hover:underline" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-[11px] text-zinc-500 text-center leading-relaxed" data-testid="login-legal-consent">
              By continuing you agree to our{" "}
              <Link to="/terms" className="link-underline font-medium text-zinc-700" data-testid="login-terms-link">Terms</Link>{" "}
              and{" "}
              <Link to="/privacy" className="link-underline font-medium text-zinc-700" data-testid="login-privacy-link">Privacy Policy</Link>.
            </div>
          </form>

          <div className="my-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            <div className="flex-1 h-px bg-zinc-200" />
            or
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <GoogleButton />

          <div className="mt-8 text-xs text-zinc-500">
            No account?{" "}
            <Link to="/register" className="link-underline font-medium text-zinc-900" data-testid="login-go-register">
              Create one with your email
            </Link>
          </div>

          <div className="mt-8 border-t border-zinc-200 pt-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">Need access?</div>
            <div className="border border-zinc-200 p-4">
              <div className="text-sm font-medium text-zinc-900">Request a demo</div>
              <div className="text-xs text-zinc-500 mt-1 mb-3">
                Contact <span className="font-mono text-zinc-700" data-testid="login-contact-number">+91 91546 37357</span> on WhatsApp
              </div>
              <a
                href="https://wa.me/919154637357?text=Request%20for%20demo"
                target="_blank"
                rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white py-2.5 text-sm font-semibold hover:bg-[#20BA5A] transition-colors"
                data-testid="login-request-demo-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
                </svg>
                Request Demo on WhatsApp
              </a>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
